import { z } from 'zod';
import {
  assignmentSchema,
  type Assignment,
  type AssignmentListQuery,
  type AssignmentResponseInput,
  type CreateAssignmentInput,
  type Page,
} from '@teentack/shared';
import { post } from '@/lib/api-client';
import { fetchPage } from '@/lib/api-page';

const createResponseSchema = z.object({ assignments: z.array(assignmentSchema) });

export function fetchMyAssignments(
  query: Partial<AssignmentListQuery> = {},
): Promise<Page<Assignment>> {
  return fetchPage('/assignments/me', assignmentSchema, query);
}

export function fetchPosterAssignments(
  query: Partial<AssignmentListQuery> = {},
): Promise<Page<Assignment>> {
  return fetchPage('/assignments', assignmentSchema, query);
}

/**
 * Assigning is not safe to repeat, so the api demands an `Idempotency-Key`.
 * The key is generated per attempt and reused across retries of that attempt,
 * which is what makes a double-tap harmless.
 */
export async function createAssignments(
  input: CreateAssignmentInput,
  idempotencyKey: string,
): Promise<Assignment[]> {
  const result = await post<unknown>('/assignments', input, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });
  return createResponseSchema.parse(result).assignments;
}

export async function respondToAssignment(
  assignmentId: string,
  input: AssignmentResponseInput,
): Promise<Assignment> {
  return assignmentSchema.parse(
    await post<unknown>(`/assignments/${assignmentId}/response`, input),
  );
}

/* -------------------------------------------------------------------------- */
/* The completion loop [Teen 16/18, Job Poster 26-28/31]                      */
/* -------------------------------------------------------------------------- */

/** The teen says the work is done. */
export async function completeAssignment(assignmentId: string): Promise<Assignment> {
  return assignmentSchema.parse(await post<unknown>(`/assignments/${assignmentId}/complete`, {}));
}

/** The employer signs it off. Money does not move here. */
export async function approveAssignment(assignmentId: string): Promise<Assignment> {
  return assignmentSchema.parse(await post<unknown>(`/assignments/${assignmentId}/approve`, {}));
}

/**
 * Releasing payment is a separate act, and not safe to repeat — the api demands
 * an `Idempotency-Key`, so a second click cannot pay twice.
 */
export async function releasePayment(
  assignmentId: string,
  idempotencyKey: string,
): Promise<Assignment> {
  return assignmentSchema.parse(
    await post<unknown>(`/assignments/${assignmentId}/release-payment`, {}, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  );
}
